import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EventProgramSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const programContent = {
    ar: {
      title: "ماذا يتضمن يوم الفعالية؟",
      sideCornersTitle: "الأركان الجانبية",
      centralCeremonyTitle: 'برنامج حفل "عام النصر" في إسطنبول يشمل:',
      sideCorners: [
        {
          id: 'children',
          title: 'ركن الأطفال "جيل الغد"',
          description: 'مساحة للتعرف على سوريا، صناعة رؤية للمستقبل.',
          icon: 'Smile'
        },
        {
          id: 'art',
          title: 'ركن الفن والثورة',
          description: 'أعمال فنية توثّق مراحل الثورة والتحرير.',
          icon: 'Palette'
        }
      ],
      centralCeremony: [
        {
          id: 'national_anthem',
          title: 'النشيد الوطني',
          summary: 'افتتاح يهيّئ لاستعادة معنى هذا اليوم ورمزيته.',
          description: 'افتتاح يهيّئ لاستعادة معنى هذا اليوم ورمزيته.',
          icon: 'Music',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        },
        {
          id: 'opening',
          title: 'الكلمة الرسمية',
          summary: 'استعراض موجز لدلالات يوم التحرير ومعناه للسوريين.',
          description: 'استعراض موجز لدلالات يوم التحرير ومعناه للسوريين.',
          icon: 'Mic',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        },
        {
          id: 'promo',
          title: 'عرض "سردية سوريا الثورة"',
          summary: 'عرض يروي قصة الثورة السورية من خلال السرد والحكاية.',
          description: 'عرض يروي قصة الثورة السورية من خلال السرد والحكاية.',
          icon: 'Video',
          color: 'text-accent',
          bgColor: 'bg-accent/10'
        },
        {
          id: 'theater',
          title: 'العرض المسرحي',
          summary: 'سكيتش يجسّد رحلة السوريين من القمع والتهجير إلى العودة والنهوض.',
          description: 'سكيتش يجسّد رحلة السوريين من القمع والتهجير إلى العودة والنهوض.',
          icon: 'Film',
          color: 'text-success',
          bgColor: 'bg-success/10'
        },
        {
          id: 'songs',
          title: 'أصواتنا الشابّة',
          summary: 'هتافات وأهازيج الثورة التي حفظت روحها الأولى.',
          description: 'هتافات وأهازيج الثورة التي حفظت روحها الأولى.',
          icon: 'Music',
          color: 'text-warning',
          bgColor: 'bg-warning/10'
        },
        {
          id: 'seminar',
          title: 'ندوة "دور مجتمع الشتات في الإعمار"',
          summary: 'حوار حول مدى أهمية تمكن مجتمع الجالية السورية في تركيا؟ وسبل نقل الفائدة إلى الوطن الأم..',
          description: 'حوار حول مدى أهمية تمكن مجتمع الجالية السورية في تركيا؟ وسبل نقل الفائدة إلى الوطن الأم..',
          icon: 'Users',
          color: 'text-accent',
          bgColor: 'bg-accent/10'
        },
        {
          id: 'panorama',
          title: 'العرض البانورامي',
          summary: 'عرض بصري يلمّ محطات ورموزًا من مسار الثورة والتحرير.',
          description: 'عرض بصري يلمّ محطات ورموزًا من مسار الثورة والتحرير.',
          icon: 'Image',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        }
      ],
      readMoreText: 'اقرأ المزيد',
      readLessText: 'اقرأ أقل',
      shareText: 'شارك هذا الحدث'
    },
    en: {
      title: "What does the event day include?",
      sideCornersTitle: "Side Corners",
      centralCeremonyTitle: "Central Ceremony",
      sideCorners: [
        {
          id: 'children',
          title: 'Children\'s Corner "Syria Tomorrow"',
          description: 'A space for imagination, play, and creating a vision for the future.',
          icon: 'Smile'
        },
        {
          id: 'art',
          title: 'Art and Revolution Corner',
          description: 'Artistic works documenting the stages of the revolution and liberation.',
          icon: 'Palette'
        },
        {
          id: 'kitchen',
          title: 'Syrian Kitchen Corner',
          description: 'Introducing Syrian identity through its flavors.',
          icon: 'Utensils'
        }
      ],
      centralCeremony: [
        {
          id: 'opening',
          title: 'Official opening and first anniversary speech of liberation',
          summary: 'Welcome speech and official opening of the event',
          description: 'Official opening of the event with a welcome speech commemorating the first anniversary of Liberation Day and the importance of this day in Syrian history.',
          icon: 'Mic',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        },
        {
          id: 'promo',
          title: 'Show "Syria\'s Revolution Narrative"',
          summary: 'A presentation that tells the story of the Syrian revolution through narrative and storytelling.',
          description: 'A presentation that tells the story of the Syrian revolution through narrative and storytelling.',
          icon: 'Video',
          color: 'text-accent',
          bgColor: 'bg-accent/10'
        },
        {
          id: 'theater',
          title: 'Theater Show',
          summary: 'A theatrical performance telling the story of the revolution and liberation',
          description: 'An artistic theatrical performance that tells the story of the Syrian revolution and the stages of liberation, expressing the human experience and transformation that occurred in the lives of Syrians.',
          icon: 'Film',
          color: 'text-success',
          bgColor: 'bg-success/10'
        },
        {
          id: 'songs',
          title: 'Revolutionary Songs and Chants from the Syrian Revolution March',
          summary: 'Performance of songs and chants that accompanied the Syrian Revolution march',
          description: 'A performance of revolutionary songs and chants that accompanied the Syrian Revolution march, expressing the spirit of resistance, hope, and solidarity.',
          icon: 'Music',
          color: 'text-warning',
          bgColor: 'bg-warning/10'
        },
        {
          id: 'panorama',
          title: 'Panorama Show',
          summary: 'A panoramic show documenting the stages of the revolution and liberation',
          description: 'An artistic panoramic show that documents the stages of the Syrian revolution and liberation, presenting the scenes and experiences that shaped the Syrian people\'s journey toward freedom.',
          icon: 'Image',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        }
      ],
      readMoreText: 'Read More',
      readLessText: 'Read Less',
      shareText: 'Share this event'
    },
    tr: {
      title: "Etkinlik günü ne içerir?",
      sideCornersTitle: "Yan Köşeler",
      centralCeremonyTitle: "Merkezi Tören",
      sideCorners: [
        {
          id: 'children',
          title: 'Çocuk Köşesi "Yarının Suriye\'si"',
          description: 'Hayal gücü, oyun ve gelecek için bir vizyon yaratma alanı.',
          icon: 'Smile'
        },
        {
          id: 'art',
          title: 'Sanat ve Devrim Köşesi',
          description: 'Devrim ve kurtuluş aşamalarını belgeleyen sanatsal eserler.',
          icon: 'Palette'
        },
      ],
      centralCeremony: [
        {
          id: 'national_anthem',
          title: 'Milli Marş',
          summary: 'Bu günün anlamını ve sembolizmini yeniden kazandırmaya hazırlayan bir açılış.',
          description: 'Bu günün anlamını ve sembolizmini yeniden kazandırmaya hazırlayan bir açılış.',
          icon: 'Music',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        },
        {
          id: 'opening',
          title: 'Resmi açılış ve kurtuluşun ilk yıldönümü konuşması',
          summary: 'Hoş geldiniz konuşması ve etkinliğin resmi açılışı',
          description: 'Kurtuluş Günü\'nün ilk yıldönümünü anan ve bu günün Suriye tarihindeki önemini vurgulayan hoş geldiniz konuşmasıyla etkinliğin resmi açılışı.',
          icon: 'Mic',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        },
        {
          id: 'promo',
          title: 'Gösteri "Suriye Devrimi Anlatısı"',
          summary: 'Suriye devriminin hikayesini anlatı ve hikaye anlatımı yoluyla anlatan bir sunum.',
          description: 'Suriye devriminin hikayesini anlatı ve hikaye anlatımı yoluyla anlatan bir sunum.',
          icon: 'Video',
          color: 'text-accent',
          bgColor: 'bg-accent/10'
        },
        {
          id: 'theater',
          title: 'Tiyatro Gösterisi',
          summary: 'Devrim ve kurtuluş hikayesini anlatan bir tiyatro gösterisi',
          description: 'Suriye devriminin ve kurtuluş aşamalarının hikayesini anlatan sanatsal bir tiyatro gösterisi, Suriyelilerin hayatlarında meydana gelen insani deneyimi ve dönüşümü ifade eder.',
          icon: 'Film',
          color: 'text-success',
          bgColor: 'bg-success/10'
        },
        {
          id: 'songs',
          title: 'Suriye Devrimi Yürüyüşünden Devrimci Şarkılar ve Marşlar',
          summary: 'Suriye Devrimi yürüyüşüne eşlik eden şarkılar ve marşların gösterimi',
          description: 'Suriye Devrimi yürüyüşüne eşlik eden devrimci şarkılar ve marşların gösterimi, direniş, umut ve dayanışma ruhunu ifade eder.',
          icon: 'Music',
          color: 'text-warning',
          bgColor: 'bg-warning/10'
        },
        {
          id: 'seminar',
          title: '"Diaspora Toplumunun Yeniden İnşadaki Rolü" Semineri',
          summary: 'Türkiye\'deki Suriye toplumunun güçlenmesinin önemi ve faydaların anavatanına aktarılma yolları hakkında bir diyalog.',
          description: 'Türkiye\'deki Suriye toplumunun güçlenmesinin önemi ve faydaların anavatanına aktarılma yolları hakkında bir diyalog.',
          icon: 'Users',
          color: 'text-accent',
          bgColor: 'bg-accent/10'
        },
        {
          id: 'panorama',
          title: 'Panorama Gösterisi',
          summary: 'Devrim ve kurtuluş aşamalarını belgeleyen panoramik bir gösteri',
          description: 'Suriye devriminin ve kurtuluşun aşamalarını belgeleyen sanatsal bir panoramik gösteri, Suriye halkının özgürlüğe doğru yolculuğunu şekillendiren sahneleri ve deneyimleri sunar.',
          icon: 'Image',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        }
      ],
      readMoreText: 'Devamını Oku',
      readLessText: 'Daha Az Oku',
      shareText: 'Bu olayı paylaş'
    }
  };

  const toggleExpanded = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  const shareItem = (item) => {
    if (navigator.share) {
      navigator.share({
        title: item?.title,
        text: item?.summary || item?.title,
        url: window.location?.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      const text = `${item?.title} - ${item?.summary || ''} ${window.location?.href}`;
      navigator.clipboard?.writeText(text);
    }
  };

  const content = programContent?.[currentLanguage];

  return (
    <section id="event-program" className="py-16 lg:py-24 bg-surface" >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-memorial text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            {content?.title}
          </h2>
        </motion.div>

        {/* Side Corners */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <h3 className="font-body text-2xl md:text-3xl font-semibold text-primary mb-8 text-center">
            {content?.sideCornersTitle}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content?.sideCorners?.map((corner, index) => (
              <motion.div
                key={corner?.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card border border-border rounded-lg p-6 shadow-memorial hover:shadow-lg memorial-transition"
              >
                <div className="flex items-start space-x-5">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={corner?.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-body text-lg font-semibold text-primary mb-2">
                      {corner?.title}
                    </h4>
                    <p className="font-body text-sm text-text-secondary leading-relaxed">
                      {corner?.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Central Ceremony */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3 className="font-body text-2xl md:text-3xl font-semibold text-primary mb-8 text-center">
            {content?.centralCeremonyTitle}
          </h3>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

            <div className="space-y-8">
              {content?.centralCeremony?.map((item, index) => (
                <motion.div
                  key={item?.id}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Timeline Dot */}
                  <div className={`absolute left-6 w-4 h-4 ${item?.bgColor} border-2 border-background rounded-full flex items-center justify-center`}>
                    <div className={`w-2 h-2 ${item?.color?.replace('text-', 'bg-')} rounded-full`}></div>
                  </div>

                  {/* Event Card */}
                  <div className="ml-16 bg-card border border-border rounded-lg shadow-memorial overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-4">
                          <div className={`w-10 h-10 ${item?.bgColor} rounded-lg flex items-center justify-center`}>
                            <Icon name={item?.icon} size={20} className={item?.color} />
                          </div>
                          <div>
                            <h4 className="font-body text-lg md:text-xl font-semibold text-primary">
                              {item?.title}
                            </h4>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => shareItem(item)}
                          iconName="Share2"
                          iconPosition="left"
                          className="flex-shrink-0 share-button"
                        >
                          {content?.shareText}
                        </Button>
                      </div>

                      {item?.summary && (
                        <p className="font-body text-text-secondary mb-4">
                          {item?.summary}
                        </p>
                      )}

                      {expandedItem === item?.id && item?.description && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-border pt-4 mb-4"
                        >
                          <p className="font-body text-text-primary leading-relaxed whitespace-pre-line">
                            {item?.description}
                          </p>
                        </motion.div>
                      )}

                      {/* {item?.description && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleExpanded(item?.id)}
                          iconName={expandedItem === item?.id ? 'ChevronUp' : 'ChevronDown'}
                          iconPosition="right"
                        >
                          {expandedItem === item?.id ? content?.readLessText : content?.readMoreText}
                        </Button>
                      )} */}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section >
  );
};

export default EventProgramSection;

